// src/routes/AppRoutes.tsx
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAppSelector } from '../hooks/useAppSelector';

// Pages
import Dashboard from '../pages/Dashboard/Dashboard';
import BoardPage from '../pages/BoardPage/BoardPage';
import SprintBoard from '../pages/SprintBoard/SprintBoard';
import SprintDashboard from '../pages/SprintDashboard/SprintDashboard';
import SprintManagement from '../pages/SprintManagement/SprintManagement';
import Analytics from '../pages/Analytics/Analytics';
import TimeTrackingPage from '../pages/TimeTracking/TimeTrackingPage';
import Team from '../pages/Team/Team';
import TeamsPage from '../pages/Teams/TeamsPage';
import Calendar from '../pages/Calendar/Calendar';
import MyWork from '../pages/MyWork/MyWork';
import Profile from '../pages/Profile/Profile';
import Settings from '../pages/Settings/Settings';
import BugTracker from '../pages/BugTracker/BugTracker';
import WorkspaceList from '../pages/Workspaces/WorkspaceList';
import WorkspaceDashboard from '../pages/Workspaces/WorkspaceDashboard';
import Login from '../pages/Login/Login';
import Signup from '../pages/Signup/Signup';
import AcceptInvite from '../pages/AcceptInvite/AcceptInvite';
import ForgotPassword from '../pages/ForgotPassword/ForgotPassword';
import ResetPassword from '../pages/ResetPassword/ResetPassword';
import MainLayout from '../components/layout/MainLayout';

// Protected Route Component - Only allows authenticated users
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes - accessible by anyone */}
        <Route path="/" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/invite/:token" element={<AcceptInvite />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Protected routes - require authentication */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
        </Route>

        {/* Workspace Routes */}
        <Route
          path="/workspaces"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<WorkspaceList />} />
        </Route>

        <Route
          path="/workspace/:id"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<WorkspaceDashboard />} />
        </Route>

        <Route
          path="/boards/:boardId"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<BoardPage />} />
        </Route>

        <Route
          path="/workspace/:id/teams"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<TeamsPage />} />
        </Route>

        {/* Workspace-agnostic entry point for the Favorites sidebar link -
            TeamsPage already falls back to state.workspaces.currentWorkspace
            when there's no :id route param. */}
        <Route
          path="/teams"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<TeamsPage />} />
        </Route>

        <Route
          path="/sprint-board"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<SprintBoard />} />
        </Route>

        <Route
          path="/sprint-dashboard"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<SprintDashboard />} />
        </Route>

        <Route
          path="/boards/:boardId/sprint-dashboard"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<SprintDashboard />} />
        </Route>

        {/* Sprint Management Routes */}
        <Route
          path="/boards/:boardId/sprints"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<SprintManagement />} />
        </Route>

        <Route
          path="/boards/:boardId/sprints/:sprintId"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<SprintManagement />} />
        </Route>

        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Analytics />} />
        </Route>

        <Route
          path="/time-tracking"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<TimeTrackingPage />} />
        </Route>

        <Route
          path="/team"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Team />} />
        </Route>

        <Route
          path="/calendar"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Calendar />} />
        </Route>

        <Route
          path="/my-work"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<MyWork />} />
        </Route>

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Profile />} />
        </Route>

        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Settings />} />
        </Route>

        <Route
          path="/bug-tracker"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<BugTracker />} />
        </Route>

        {/* 404 route - redirect to home/login */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRoutes;
